import React, { useState } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useAlertContext } from '../context/AlertContext';
import { FaShoppingCart, FaStar, FaCheck } from 'react-icons/fa';

const ResourceCard = ({ resource }) => {
  const { addToCart, isInCart } = useCart();
  const { user } = useAuth();
  const { showAlert } = useAlertContext();
  const [isAdding, setIsAdding] = useState(false);

  const handleAddToCart = async (e) => {
    e.preventDefault(); // Prevent navigation to details page when clicking button
    
    if (!user) {
      showAlert({
        type: 'info',
        title: 'Login Required',
        message: 'Please log in to save resources to your cart.'
      });
      return;
    }

    if (isAdding) return;

    try {
      setIsAdding(true);
      
      // Validate resource data
      if (!resource.id || !resource.title) {
        console.error('Invalid resource data:', resource);
        showAlert({
          type: 'error',
          title: 'Error',
          message: 'Invalid resource data. Please try again.'
        });
        return;
      }

      // Format the resource data to match the server's expected format
      const formattedResource = {
        resourceId: resource.id,
        title: resource.title,
        description: resource.description || '',
        category: resource.category || '',
        image: resource.image || '',
        link: resource.link || '',
        rating: resource.rating || 0
      };

      await addToCart(formattedResource);
    } catch (error) {
      console.error('Error adding to cart:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: 'Failed to add to cart. Please try again.'
      });
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="group relative bg-white/70 backdrop-blur-md rounded-3xl shadow-lg border border-white/60 overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 h-full flex flex-col">
      {/* Image Section */}
      <div className="relative h-48 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10 opacity-60 group-hover:opacity-40 transition-opacity"></div>
        <img 
          src={resource.image} 
          alt={resource.title} 
          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
        />
        
        {/* Category Badge */}
        <div className="absolute top-4 left-4 z-20">
             <span className="px-3 py-1 bg-white/20 backdrop-blur-md border border-white/30 text-white text-xs font-bold uppercase tracking-wider rounded-full shadow-sm">
                {resource.category}
             </span>
        </div>

        {/* Rating Badge */}
        <div className="absolute bottom-4 left-4 z-20 flex items-center gap-1 bg-black/40 backdrop-blur-md px-2 py-1 rounded-lg border border-white/20">
             <FaStar className="text-yellow-400 w-3 h-3" />
             <span className="text-white text-xs font-bold">{resource.rating}</span>
        </div>
        
        {/* Premium Corner (Visual Only) */}
        {resource.rating >= 4.5 && (
            <div className="absolute top-0 right-0 z-20">
                <div className="bg-gradient-to-bl from-yellow-400 to-transparent w-16 h-16 opacity-80 rounded-bl-[3rem]"></div>
            </div>
        )}
      </div>
      
      {/* Content Section */}
      <div className="p-6 flex-1 flex flex-col">
        <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 leading-tight group-hover:text-indigo-600 transition-colors">
            {resource.title}
        </h3>
        
        <p className="text-sm text-gray-600 mb-6 line-clamp-2 leading-relaxed flex-1">
            {resource.description}
        </p>

        {/* Action Button */}
        <button
            onClick={handleAddToCart}
            disabled={isInCart(resource.id) || isAdding}
            className={`w-full py-3 px-4 rounded-xl font-bold text-sm shadow-md transition-all flex items-center justify-center gap-2 transform active:scale-95 ${
              isInCart(resource.id)
                ? 'bg-green-100 text-green-700 border border-green-200 cursor-default'
                : isAdding
                ? 'bg-gray-100 text-gray-400 cursor-wait'
                : 'bg-gradient-to-r from-indigo-600 to-indigo-500 text-white hover:shadow-lg hover:shadow-indigo-500/30'
            }`}
          >
            {isInCart(resource.id) ? (
                <>
                    <FaCheck className="w-3.5 h-3.5" /> Saved to Library
                </>
            ) : isAdding ? (
                'Saving...'
            ) : (
                <>
                    <FaShoppingCart className="w-3.5 h-3.5" /> Add to Library
                </>
            )}
        </button>
      </div>
    </div>
  );
};

export default ResourceCard; 